import styled, { css } from 'styled-components';
import { spaces, colors } from '@99designs/ui/settings';
import * as CSS from 'csstype';
import { ResponsiveProp, resolveResponsiveProp } from 'libs/utils/responsiveProps';
import { joinAndFilter } from 'libs/utils/joinAndFilter/joinAndFilter';

type TLengthSystem = string | number | 0;

export interface BoxProps {
    // Display
    display?: CSS.Property.Display;

    width?: ResponsiveProp<CSS.Property.Width<string>>;
    height?: ResponsiveProp<CSS.Property.Height<string>>;
    maxWidth?: ResponsiveProp<CSS.Property.MaxWidth<string>>;
    maxHeight?: ResponsiveProp<CSS.Property.MaxHeight<string>>;

    position?: CSS.Property.Position;

    // Spaces
    margin?: ResponsiveProp<spaces.Space>;
    marginX?: ResponsiveProp<spaces.Space>;
    marginY?: ResponsiveProp<spaces.Space>;
    marginBottom?: ResponsiveProp<spaces.Space>;
    marginTop?: ResponsiveProp<spaces.Space>;
    marginLeft?: ResponsiveProp<spaces.Space>;
    marginRight?: ResponsiveProp<spaces.Space>;

    padding?: ResponsiveProp<spaces.Space>;
    paddingX?: ResponsiveProp<spaces.Space>;
    paddingY?: ResponsiveProp<spaces.Space>;
    paddingBottom?: ResponsiveProp<spaces.Space>;
    paddingTop?: ResponsiveProp<spaces.Space>;
    paddingLeft?: ResponsiveProp<spaces.Space>;
    paddingRight?: ResponsiveProp<spaces.Space>;

    // Flexbox
    flexDirection?: ResponsiveProp<CSS.Property.FlexDirection>;
    flexWrap?: CSS.Property.FlexWrap;
    justifyContent?: CSS.Property.JustifyContent;
    alignItems?: CSS.Property.AlignItems;
    alignContent?: CSS.Property.AlignContent;
    order?: CSS.Property.Order;
    flex?: CSS.Property.Flex<TLengthSystem>;
    flexGrow?: CSS.Property.FlexGrow;
    flexShrink?: CSS.Property.FlexShrink;
    alignSelf?: CSS.Property.AlignSelf;

    // Color
    background?: CSS.Property.Background<string> | colors.Color;
    color?: CSS.Property.Color | colors.Color;
}

const displayStyle = (display: CSS.Property.Display) =>
    css`
        display: ${display};
    `;

const widthStyle = (width: ResponsiveProp<CSS.Property.Width<string>>) =>
    css`
        ${resolveResponsiveProp(width, 'width')}
    `;

const heightStyle = (height: ResponsiveProp<CSS.Property.Height<string>>) =>
    css`
        ${resolveResponsiveProp(height, 'height')}
    `;

const maxWidthStyle = (maxWidth: ResponsiveProp<CSS.Property.MaxWidth<string>>) =>
    css`
        ${resolveResponsiveProp(maxWidth, 'max-width')}
    `;

const maxHeightStyle = (maxHeight: ResponsiveProp<CSS.Property.MaxHeight<string>>) =>
    css`
        ${resolveResponsiveProp(maxHeight, 'max-height')}
    `;

const positionStyle = (position: CSS.Property.Position) =>
    css`
        position: ${position};
    `;

const flexDirectionStyle = (flexDirection: ResponsiveProp<CSS.Property.FlexDirection>) =>
    css`
        ${resolveResponsiveProp(flexDirection, 'flex-direction')}
    `;

const flexWrapStyle = (flexWrap: CSS.Property.FlexWrap) => css`
    flex-wrap: ${flexWrap};
`;

const justifyContentStyle = (justifyContent: CSS.Property.JustifyContent) => css`
    justify-content: ${justifyContent};
`;

const alignItemsStyle = (alignItems: CSS.Property.AlignItems) => css`
    align-items: ${alignItems};
`;

const alignContentStyle = (alignContent: CSS.Property.AlignContent) => css`
    align-content: ${alignContent};
`;

const orderStyle = (order: CSS.Property.Order) => css`
    order: ${order};
`;

const flexStyle = (flex: CSS.Property.Flex<TLengthSystem>) => css`
    flex: ${flex};
`;

const flexGrowStyle = (flexGrow: CSS.Property.FlexGrow) => css`
    flex-grow: ${flexGrow};
`;

const flexShrinkStyle = (flexShrink: CSS.Property.FlexShrink) => css`
    flex-shrink: ${flexShrink};
`;

const alignSelfStyle = (alignSelf: CSS.Property.AlignSelf) => css`
    align-self: ${alignSelf};
`;

const backgroundStyle = (background: CSS.Property.Background<string> | colors.Color) => css`
    background: ${colors[background as colors.Color] || background};
`;

const colorStyle = (color: CSS.Property.Color | colors.Color) => css`
    color: ${colors[color as colors.Color] || color};
`;

const Box = styled.div<BoxProps>`
    ${({ display }) => display && displayStyle(display)}

    ${({ width }) => width && widthStyle(width)}
    ${({ height }) => height && heightStyle(height)}
    ${({ maxWidth }) => maxWidth && maxWidthStyle(maxWidth)}
    ${({ maxHeight }) => maxHeight && maxHeightStyle(maxHeight)}

    ${({ position }) => position && positionStyle(position)}

    ${({ margin, marginX, marginY, marginBottom, marginTop, marginLeft, marginRight }) => {
        const resolvedMarginTop = marginTop || marginY || margin;
        const resolvedMarginBottom = marginBottom || marginY || margin;
        const resolvedMarginLeft = marginLeft || marginX || margin;
        const resolvedMarginRight = marginRight || marginX || margin;

        return joinAndFilter(
            resolvedMarginTop &&
                resolveResponsiveProp(resolvedMarginTop, 'margin-top', spaces.sizes),
            resolvedMarginBottom &&
                resolveResponsiveProp(resolvedMarginBottom, 'margin-bottom', spaces.sizes),
            resolvedMarginLeft &&
                resolveResponsiveProp(resolvedMarginLeft, 'margin-left', spaces.sizes),
            resolvedMarginRight &&
                resolveResponsiveProp(resolvedMarginRight, 'margin-right', spaces.sizes)
        );
    }}

    ${({ padding, paddingX, paddingY, paddingBottom, paddingTop, paddingLeft, paddingRight }) => {
        const resolvedPaddingTop = paddingTop || paddingY || padding;
        const resolvedPaddingBottom = paddingBottom || paddingY || padding;
        const resolvedPaddingLeft = paddingLeft || paddingX || padding;
        const resolvedPaddingRight = paddingRight || paddingX || padding;

        return joinAndFilter(
            resolvedPaddingTop &&
                resolveResponsiveProp(resolvedPaddingTop, 'padding-top', spaces.sizes),
            resolvedPaddingBottom &&
                resolveResponsiveProp(resolvedPaddingBottom, 'padding-bottom', spaces.sizes),
            resolvedPaddingLeft &&
                resolveResponsiveProp(resolvedPaddingLeft, 'padding-left', spaces.sizes),
            resolvedPaddingRight &&
                resolveResponsiveProp(resolvedPaddingRight, 'padding-right', spaces.sizes)
        );
    }}

    ${({ flexDirection }) => flexDirection && flexDirectionStyle(flexDirection)}
    ${({ flexWrap }) => flexWrap && flexWrapStyle(flexWrap)}
    ${({ justifyContent }) => justifyContent && justifyContentStyle(justifyContent)}
    ${({ alignItems }) => alignItems && alignItemsStyle(alignItems)}
    ${({ alignContent }) => alignContent && alignContentStyle(alignContent)}
    ${({ order }) => order && orderStyle(order)}
    ${({ flex }) => flex && flexStyle(flex)}
    ${({ flexGrow }) => flexGrow && flexGrowStyle(flexGrow)}
    ${({ flexShrink }) => flexShrink && flexShrinkStyle(flexShrink)}
    ${({ alignSelf }) => alignSelf && alignSelfStyle(alignSelf)}
    ${({ background }) => background && backgroundStyle(background)}
    ${({ color }) => color && colorStyle(color)}
`;

export default Box;
